import { useMemo, useState, useEffect, useRef } from "react";
import { AIAssistantSidebar } from "../ai-assistant-sidebar";
import { useBrandVoiceData } from "../../hooks/brand/useBrandVoiceData";
import { toast } from "sonner";
import {
  generateBrandVoice,
  saveBrandVoice,
  setCurrentBrandVoiceVersion,
  generateTestContent,
  getBrandVoiceVersion,
  type VoiceProfile,
  type GeneratedAttributes,
  type BeforeAfterExample,
} from "../../services/brand/brand-voice-service";
import { AlertCircle, Loader2, History } from "lucide-react";
import { BrandControlsShell } from "./redesign/brand-controls-shell";
import "./redesign/brand-controls-redesign.css";

interface BrandVoiceProfileProps {
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (collapsed: boolean) => void;
}

type EditableField =
  | "tone"
  | "sentenceStructure"
  | "vocabulary"
  | "perspective"
  | "emphasis";

export function BrandVoiceProfile({
  isSidebarCollapsed = false,
  onSidebarToggle,
}: BrandVoiceProfileProps) {
  const { data, isLoading, error, refresh, setData } = useBrandVoiceData();
  const { currentProfile, versionHistory, generatedAttributes } = data;

  const [activeTab, setActiveTab] = useState("overview");
  const [sampleText, setSampleText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGeneratedProfile, setShowGeneratedProfile] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // Live rewrite (Overview §02) state machine.
  const [rwInput, setRwInput] = useState("");
  const [rwPhase, setRwPhase] = useState<"idle" | "working" | "done">("idle");
  const [rwResult, setRwResult] = useState("");

  // Transient "Saved" confirmation for the save buttons.
  const [savedFlash, setSavedFlash] = useState(false);
  const didInitTab = useRef(false);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [viewingVersion, setViewingVersion] = useState<string | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [beforeAfter, setBeforeAfter] = useState<BeforeAfterExample | null>(null);

  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [editValue, setEditValue] = useState("");
  const [focusAttribute, setFocusAttribute] = useState<string>("all");

  const [isAddingTerm, setIsAddingTerm] = useState(false);
  const [newTerm, setNewTerm] = useState("");

  const [testTitle, setTestTitle] = useState("");
  const [testContent, setTestContent] = useState("");
  const [isTesting, setIsTesting] = useState(false);

  // A brand with no saved voice comes back as the backend "default" seed
  // (status "default", version "0.0", placeholder attributes). Treat that as
  // no-profile so the shared empty state shows on Overview / Advanced /
  // History and Quick Setup becomes the landing tab.
  const hasProfile =
    !!currentProfile &&
    !!generatedAttributes &&
    (currentProfile.status || "").toLowerCase() !== "default" &&
    String(currentProfile.version || "") !== "0.0";

  // Brand label + domain for the header / analyzing copy.
  const brand = useMemo(() => {
    const raw = (localStorage.getItem("bm_brand") || "").trim();
    if (!raw || raw.toLowerCase() === "default") return "your brand";
    return raw.replace(/\b\w/g, (c) => c.toUpperCase());
  }, []);

  const domain = useMemo(() => {
    const u = (websiteUrl || "").trim();
    const d = u.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    return d || "your site";
  }, [websiteUrl]);

  // Land the user in Quick Setup on first load when there is no saved voice.
  useEffect(() => {
    if (isLoading || didInitTab.current) return;
    didInitTab.current = true;
    setActiveTab(hasProfile ? "overview" : "setup");
  }, [isLoading, hasProfile]);

  // Responsive right-margin that reserves AI sidebar space on lg+.
  // Below lg the AI sidebar is hidden so content fills full width.
  const contentMarginClass = isSidebarCollapsed ? "lg:mr-12" : "lg:mr-[380px]";

  const startEditing = (field: EditableField) => {
    if (!currentProfile) return;
    setEditingField(field);
    setEditValue(String((currentProfile as any)[field] || ""));
    setFocusAttribute(field);
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue("");
  };

  const applyEditLocal = () => {
    if (!currentProfile || !editingField) return;
    const updated: VoiceProfile = {
      ...currentProfile,
      [editingField]: editValue,
    } as any;
    setData((prev) => ({ ...prev, currentProfile: updated }));
    cancelEditing();
  };

  const addAvoidTermLocal = () => {
    if (!currentProfile) return;
    const t = (newTerm || "").trim();
    if (!t) return;

    const exists = (currentProfile.avoidList || []).some(
      (x) => String(x).toLowerCase() === t.toLowerCase()
    );
    if (exists) return;

    const updated: VoiceProfile = {
      ...currentProfile,
      avoidList: [...(currentProfile.avoidList || []), t],
    };
    setData((prev) => ({ ...prev, currentProfile: updated }));
    setNewTerm("");
    setIsAddingTerm(false);
  };

  const removeAvoidTermLocal = (term: string) => {
    if (!currentProfile) return;
    const updated: VoiceProfile = {
      ...currentProfile,
      avoidList: (currentProfile.avoidList || []).filter((t) => t !== term),
    };
    setData((prev) => ({ ...prev, currentProfile: updated }));
  };

  const handleAutoGenerate = async () => {
    const url = (websiteUrl || "").trim();
    if (!url) return;

    try {
      setIsGenerating(true);
      setShowGeneratedProfile(false);
      setViewingVersion(null);

      const res = await generateBrandVoice({
        websiteUrl: url,
        sampleText,
        file: selectedFile,
      });

      setData((prev) => ({
        ...prev,
        currentProfile: res.draftProfile as VoiceProfile,
        generatedAttributes: res.generatedAttributes as GeneratedAttributes,
      }));

      setBeforeAfter(res.beforeAfterExample || null);

      setEditingField(null);
      setEditValue("");
      setFocusAttribute("all");
      setIsAddingTerm(false);
      setNewTerm("");

      setTestTitle("");
      setTestContent("");

      setShowGeneratedProfile(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to generate profile");
      setShowGeneratedProfile(false);
      setBeforeAfter(null);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveProfileAsNewVersion = async (changesSummary?: string) => {
    if (!data.currentProfile || !data.generatedAttributes) return;

    const profileToSave: VoiceProfile = {
      ...data.currentProfile,
      beforeAfterExample:
        beforeAfter || (data.currentProfile as any).beforeAfterExample || null,
      testContentExample: testContent
        ? { title: testTitle || "Test Content", content: testContent }
        : (data.currentProfile as any).testContentExample,
    } as any;

    try {
      await saveBrandVoice({
        profile: profileToSave,
        generatedAttributes: data.generatedAttributes,
        changesSummary:
          changesSummary || data.currentProfile.changesSummary || "Profile updated",
      });

      setShowGeneratedProfile(false);
      setViewingVersion(null);
      await refresh();
      toast.success("Profile saved successfully");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save profile");
    }
  };

  const handleRollback = async (version: string) => {
    try {
      await setCurrentBrandVoiceVersion(version);
      setViewingVersion(null);
      await refresh();
      toast.success(`Set version v${version} as current`);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to set current version"
      );
    }
  };

  const handleGenerateTestContent = async (
    profile: VoiceProfile,
    attrs: GeneratedAttributes,
    focus: string
  ) => {
    try {
      setIsTesting(true);
      const res = await generateTestContent({
        profile,
        generatedAttributes: attrs,
        scenario:
          "Write a short product update announcement that would appear on a brand website.",
        focusAttribute: focus || "all",
      });
      setTestTitle(res.title || "Test Content");
      setTestContent(res.content || "");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to generate test content"
      );
    } finally {
      setIsTesting(false);
    }
  };

  const handleViewVersion = async (version: string) => {
    setViewingVersion(version);
    try {
      const res = await getBrandVoiceVersion(version);

      setData((prev) => ({
        ...prev,
        currentProfile: res.profile,
        generatedAttributes: res.generatedAttributes,
      }));

      setShowGeneratedProfile(true);

      setActiveTab("advanced");
      setBeforeAfter((res.profile as any)?.beforeAfterExample || null);
      setEditingField(null);
      setIsAddingTerm(false);
      setNewTerm("");
      setFocusAttribute("all");

      const savedExample = (res.profile as any)?.testContentExample;
      if (savedExample?.content) {
        setTestTitle(savedExample.title || "Test Content");
        setTestContent(savedExample.content);
      } else {
        await handleGenerateTestContent(
          res.profile as any,
          res.generatedAttributes as any,
          "all"
        );
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load version");
    }
  };

  // Live rewrite: reuse generateTestContent with a rewrite scenario.
  const handleRewrite = async () => {
    if (!currentProfile || !generatedAttributes) return;
    const input = rwInput.trim();
    if (!input) return;
    try {
      setRwPhase("working");
      const res = await generateTestContent({
        profile: currentProfile,
        generatedAttributes,
        scenario:
          `Rewrite the following copy in the brand's voice, preserving its meaning ` +
          `and steering around the avoid-list terms. Return only the rewritten copy.\n\n"""${input}"""`,
        focusAttribute: "all",
      });
      setRwResult(res.content || "");
      setRwPhase("done");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to rewrite");
      setRwPhase("idle");
    }
  };

  const handleSaveFlash = async (summary?: string) => {
    await handleSaveProfileAsNewVersion(summary);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1800);
  };

  const handleResetSetup = () => {
    setShowGeneratedProfile(false);
    setBeforeAfter(null);
  };

  if (isLoading) {
    return (
      <div style={{ backgroundColor: "var(--background)" }}>
        <div className={`transition-all duration-300 ${contentMarginClass}`}>
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="flex flex-col items-center gap-4">
              <Loader2
                className="w-8 h-8 animate-spin"
                style={{ color: "var(--signal-blue)" }}
              />
              <p style={{ fontSize: "var(--text-base)", color: "var(--muted-foreground)" }}>
                Loading brand voice data...
              </p>
            </div>
          </div>
        </div>
        <div className="hidden lg:block">
          <AIAssistantSidebar phase="brand-voice" isCollapsed={isSidebarCollapsed} onToggleCollapse={onSidebarToggle} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: "var(--background)" }}>
        <div className={`transition-all duration-300 ${contentMarginClass}`}>
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="flex flex-col items-center gap-4 max-w-md text-center">
              <AlertCircle className="w-8 h-8" style={{ color: "var(--infrared-coral)" }} />
              <p style={{ fontSize: "var(--text-base)", color: "var(--foreground)" }}>
                Failed to load brand voice data
              </p>
              <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                {error}
              </p>
            </div>
          </div>
        </div>
        <div className="hidden lg:block">
          <AIAssistantSidebar phase="brand-voice" isCollapsed={isSidebarCollapsed} onToggleCollapse={onSidebarToggle} />
        </div>
      </div>
    );
  }


  return (
    <div className="bc-root" data-kit style={{ backgroundColor: "var(--background)" }}>
      {/* Content column reserves space for the AI sidebar on lg+. */}
      <div className={`transition-all duration-300 ${contentMarginClass}`}>
        {viewingVersion && (
          <div className="px-6 md:px-16 pt-4">
            <div
              className="p-3 md:p-4 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
            >
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <History className="w-4 h-4 flex-shrink-0" style={{ color: "var(--muted-foreground)" }} />
                <span style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", fontWeight: "var(--font-weight-medium)" }}>
                  Viewing version v{viewingVersion}
                </span>
              </div>
              <button
                onClick={async () => {
                  setViewingVersion(null);
                  setBeforeAfter(null);
                  setTestTitle("");
                  setTestContent("");
                  await refresh();
                }}
                className="w-full sm:w-auto"
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "var(--foreground)",
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: "9px",
                  padding: "8px 14px",
                  cursor: "pointer",
                }}
              >
                Return to current
              </button>
            </div>
          </div>
        )}

        <BrandControlsShell
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          hasProfile={hasProfile}
          brand={brand}
          headerVersion={currentProfile?.version ?? ""}
          headerUpdatedAt={currentProfile?.updatedAt ?? ""}
          headerUsageCount={currentProfile?.usageCount ?? 0}
          goSetup={() => setActiveTab("setup")}
          overview={{
            brand,
            profile: currentProfile as VoiceProfile,
            attrs: generatedAttributes,
            beforeAfter,
            rwInput,
            onRwInput: setRwInput,
            runRewrite: handleRewrite,
            rwPhase,
            rwResult,
            goSetup: () => setActiveTab("setup"),
            goAdvanced: () => setActiveTab("advanced"),
          }}
          quickSetup={{
            websiteUrl,
            onUrl: setWebsiteUrl,
            sampleText,
            onSample: setSampleText,
            advancedOpen: showAdvancedOptions,
            toggleAdvanced: () => setShowAdvancedOptions((v) => !v),
            uploadName: selectedFile?.name ?? null,
            onUpload: setSelectedFile,
            generate: handleAutoGenerate,
            reset: handleResetSetup,
            isGenerating,
            showResult: showGeneratedProfile,
            domain,
            profile: currentProfile,
            attrs: generatedAttributes,
            beforeAfter,
            save: () => handleSaveFlash(currentProfile?.changesSummary),
            saved: savedFlash,
          }}
          advanced={{
            profile: currentProfile as VoiceProfile,
            attrs: generatedAttributes,
            editingField,
            editValue,
            onStartEdit: startEditing,
            onEditChange: setEditValue,
            onSaveEdit: applyEditLocal,
            onCancelEdit: cancelEditing,
            isAddingTerm,
            toggleAddTerm: () => setIsAddingTerm((v) => !v),
            newTerm,
            onNewTerm: setNewTerm,
            onAddTerm: addAvoidTermLocal,
            onRemoveTerm: removeAvoidTermLocal,
            isTesting,
            testTitle,
            testContent,
            onGenerateTest: () => {
              if (currentProfile && generatedAttributes)
                handleGenerateTestContent(currentProfile, generatedAttributes, focusAttribute);
            },
            onSaveVersion: () => handleSaveFlash(currentProfile?.changesSummary),
            saved: savedFlash,
          }}
          history={{
            versions: versionHistory,
            onView: handleViewVersion,
            onRollback: handleRollback,
          }}
        />
      </div>

      {/* AI Assistant Sidebar — hidden below lg */}
      <div className="hidden lg:block">
        <AIAssistantSidebar
          phase="brand-voice"
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={onSidebarToggle}
        />
      </div>
    </div>
  );
}
